import { createLookupStore, useCrudDataSource, type LookupStore } from '@botto/core';
import type CustomStore from 'devextreme/data/custom_store';
import type { Manager, Partner, SmppClient } from '../../types';
import { smppClientsAdapter } from './smppClients';
import { managersAdapter, managersStore } from '../managers';
import { partnersAdapter, partnersStore } from '../partners';

export { managersStore, partnersStore };

/** Same CustomStore instances as on /sms/accounts tabs. */
export const smppClientsStore = useCrudDataSource(smppClientsAdapter);

async function loadAllFromStore<T>(store: CustomStore<T>): Promise<T[]> {
  const result = await store.load({ skip: 0, take: 10_000 });
  if (Array.isArray(result)) return result as T[];
  const data = (result as { data?: T[] }).data;
  return data ?? [];
}

export async function fetchPartnersLookup(): Promise<Partner[]> {
  const result = await partnersAdapter.list({ offset: 0, limit: 10_000, filter: { archived: false } });
  return result.items;
}

export async function fetchManagersLookup(): Promise<Manager[]> {
  const result = await managersAdapter.list({ offset: 0, limit: 10_000, filter: { archived: false } });
  return result.items;
}

export async function fetchSmppClientsLookup(): Promise<SmppClient[]> {
  return loadAllFromStore(smppClientsStore);
}

let _partnersLookup: LookupStore<Partner> | undefined;
let _managersLookup: LookupStore<Manager> | undefined;
let _smppClientsLookup: LookupStore<SmppClient> | undefined;

export function getPartnersLookup(): LookupStore<Partner> {
  if (!_partnersLookup) {
    _partnersLookup = createLookupStore<Partner>(fetchPartnersLookup, {
      idField: 'id',
      labelField: 'legal_entity',
      format: (p) => `${p.legal_entity}${p.domain ? ` · ${p.domain}` : ''} (#${p.id})`,
    });
  }
  return _partnersLookup;
}

export function getManagersLookup(): LookupStore<Manager> {
  if (!_managersLookup) {
    _managersLookup = createLookupStore<Manager>(fetchManagersLookup, {
      idField: 'id',
      labelField: 'name',
      format: (m) => `${m.name}${m.email ? ` · ${m.email}` : ''} (#${m.id})`,
    });
  }
  return _managersLookup;
}

export function getSmppClientsLookup(): LookupStore<SmppClient> {
  if (!_smppClientsLookup) {
    _smppClientsLookup = createLookupStore<SmppClient>(fetchSmppClientsLookup, {
      idField: 'id',
      labelField: 'description',
      format: (c) => {
        const label = c.description || c.name || c.system_id || '—';
        return `${label} (#${c.id})`;
      },
    });
  }
  return _smppClientsLookup;
}

/** Refresh lookup caches from the same stores as the accounts grids. */
export async function reloadAccountsLookups(): Promise<void> {
  await Promise.all([
    getPartnersLookup().reload(),
    getManagersLookup().reload(),
    getSmppClientsLookup().reload(),
  ]);
}
